import { useState } from "react";
import { Pencil, PlusCircle, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { Button, ButtonDelete } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  Ingredient,
  IngredientPackaging,
} from "@/redux/services/inventory/inventory.type";
import { useDeleteIngredientPackagingMutation } from "@/redux/services/inventory/ingredientPackaging.api";
import { stripTrailingZeros } from "@/lib/utils";
import PackagingFormModal from "./PackagingFormModal";

interface PackagingsSectionProps {
  ingredient: Ingredient;
}

const PackagingsSection = ({ ingredient }: PackagingsSectionProps) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<IngredientPackaging | undefined>(
    undefined
  );
  const [deletePackaging] = useDeleteIngredientPackagingMutation();
  const confirm = useConfirm();

  const packagings = ingredient.packagings ?? [];

  const handleAdd = () => {
    setEditing(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (packaging: IngredientPackaging) => {
    setEditing(packaging);
    setIsFormOpen(true);
  };

  const handleDelete = async (packaging: IngredientPackaging) => {
    const ok = await confirm({
      title: `Supprimer le conditionnement "${packaging.name}" ?`,
      description: "Cette action est irréversible.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    const res = await deletePackaging({ uuid: packaging.id });
    if ("error" in res) {
      toast.error("Erreur lors de la suppression");
      return;
    }
    toast.success("Conditionnement supprimé");
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-card-foreground">
            Conditionnements
          </h3>
          <Button type="button" size="sm" variant="outline" onClick={handleAdd}>
            <PlusCircle className="h-4 w-4" />
            Ajouter
          </Button>
        </div>

        {packagings.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Aucun conditionnement défini.
          </p>
        ) : (
          <ul className="space-y-2">
            {packagings.map((packaging) => (
              <li
                key={packaging.id}
                className="flex items-center justify-between bg-muted/40 rounded-md px-3 py-2"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-card-foreground">
                      {packaging.name}
                    </p>
                    {packaging.isDefault && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
                        Par défaut
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    1 {packaging.name} ={" "}
                    {stripTrailingZeros(packaging.quantityInBase)}{" "}
                    {ingredient.baseUnit}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleEdit(packaging)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <ButtonDelete onClick={() => handleDelete(packaging)} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isFormOpen && (
        <PackagingFormModal
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          ingredientUuid={ingredient.id}
          baseUnit={ingredient.baseUnit}
          packagingToEdit={editing}
        />
      )}
    </>
  );
};

export default PackagingsSection;
